import { ApplicationController } from '@aha-app/mvc';
import $ from 'jquery';
import {
  DocumentState,
  RichTextPlugins,
  WritingAssistantProvider,
  type AhaEditorV2Controller,
  type CollaborationOptions,
  type FetchUserInfo,
  type DocumentValue,
  type EditorPlugin,
  type AhaEditor,
  AhaEditorUtils,
  MigrationError,
  AllPlugins,
  UiPlugins,
  type ReferenceAhaObject,
  type Behaviors,
  type BackwardsCompatibleDocumentValue,
} from '@aha-app/aha-editor-v2';
import initializeV2Collaboration from '../collaboration/collaboratonOptionsV2';
import type { TokenPayload } from '../collaboration/TokenManager';
import type TokenManager from '../collaboration/TokenManager';
import { showAlertMessage } from 'javascripts/messages';
import CommentsSidePanelItem from 'components/side_panels/comments/CommentsSidePanelItem';
import { randomId } from 'javascripts/random_id';
import { type UserReferenceObject } from '../plugins/ReferenceUser/types';
import AhaSharedSnippetProvider from '../plugins/SharedSnippets/SharedSnippetProviderPlugin';
import {
  type ISidePanelController as SidePanelController,
  type SidePanelItem,
  type RecordId,
} from 'javascripts/shared-types';
import KnowledgeBaseLinkRewriterPlugin from '../plugins/KnowledgeBaseLinkRewriter/KnowledgeBaseLinkRewriterPlugin';
import EmbeddedWhiteboardPlugin from '../plugins/EmbeddedWhiteboard/EmbeddedWhiteboardPlugin';
import EmbeddedReportPlugin from '../plugins/EmbeddedReport/EmbeddedReportPlugin';
import BlockedEmbeddedReportPlugin from '../plugins/EmbeddedReport/BlockedEmbeddedReportPlugin';
import AnchorLinkPlugin from '../plugins/AnchorLink/AnchorLinkPlugin';
import AhaMediaProvider from '../plugins/MediaProvider/AhaMediaProvider';
import AhaAnnotationsProvider from '../plugins/Annotations/AhaAnnotationsProvider';
import attachmentPasteTransformerFactory from '../plugins/Attachments/AttachmentPasteTransformerFactory';
import FullScreenTopBarPluginFactory, {
  type FullScreenProviderProps,
} from '../plugins/LayoutComponents/FullScreenTopBar';
import DraftManagerPluginFactory, {
  type Draft,
  type DraftManagerProps,
} from '../plugins/LayoutComponents/DraftManagerPlugin';
import { SidePanelPluginFactory } from '../plugins/SidepanelPlugin/SidePanelPlugin';
import BatchedRecordLoader from 'components/framework/BatchedRecordLoader';
import ReferenceAhaPlugin from '../plugins/ReferenceAha/ReferenceAhaPlugin';
import ReferenceUserPlugin from '../plugins/ReferenceUser/ReferenceUserPlugin';
import SetEditorControllerPlugin from '../plugins/SetEditorController/SetEditorControllerPlugin';
import AutosavePlugin, {
  type AutosavePluginProps,
} from '../plugins/Autosave/AutosavePlugin';
import HiddenFormInputPluginFactory from '../plugins/HiddenFormInput/HiddenFormInputPlugin';
import { assistantSidePanelConfiguration } from 'components/Assistant';
import { clearUnflap, unflap } from 'javascripts/util/unflap';
import AssistedEditor from 'components/Assistant/agents/WritingAgent/AssistedEditor';
import DocumentSavedStatePlugin from '../plugins/DocumentSavedStatePlugin/DocumentSavedStatePlugin';
import SelectAnnotationFromUrlPlugin from '../plugins/SelectAnnotationFromUrlPlugin/SelectAnnotationFromUrlPlugin';
import {
  BestPracticesSidebarItem,
  BEST_PRACTICES_PANEL_KEY,
} from 'components/whiteboards/best_practices_panel/BestPracticesPanel';
import AhaDocumentTemplatePluginFactory, {
  AHA_DOCUMENT_TEMPLATE_PLUGIN_ID,
  type AhaDocumentTemplatePlugin,
} from '../plugins/InsertTemplate/AhaDocumentTemplatePlugin';
import type { DocumentTypeEnum_value } from 'components/framework/models/generated/types';

export interface TemplatePickerProps {
  supportsEmbeddedWhiteboards?: boolean;
  supportsTemplates?: boolean;
}

export interface InsertTemplateConfiguration {
  showWhiteboardTemplates: boolean;
  projectId: AhaEditorFieldV2ControllerProps['projectId'];
  getSharingToken?: AhaEditorFieldV2ControllerProps['getSharingToken'];
  objectId?: AhaEditorFieldV2ControllerProps['documentId'];
  objectType: AhaEditorFieldV2ControllerProps['objectType'];
  baseURL?: string;
}

export interface AhaEditorFieldV2ControllerProps
  extends FullScreenProviderProps,
    DraftManagerProps,
    TemplatePickerProps,
    Partial<AutosavePluginProps> {
  body?: string | null;
  value?: BackwardsCompatibleDocumentValue;
  // We don't use this to inherit styling anymore, but we use it to toggle the editor props
  className?: string;
  onComponentDidMount?: Function;
  enclosingSelector?: string;
  documentCollection: string;
  documentId: RecordId;
  documentVersion: number;
  userInfo?: {
    userId: RecordId;
    displayName: string;
  };
  objectType: string;
  parentType?: string;
  parentId?: RecordId;
  connectedAccountId?: RecordId;
  collaborationBaseURL?: string;
  collaborationServerURL?: string;
  getSharingToken?: () => string;
  collaborative?: boolean;
  baseURL?: string;
  selectionURL?: string;
  selectionToken?: string;
  historyURL?: string;
  readOnly?: boolean;
  editLocked?: boolean;
  annotateOnly?: boolean;
  attachmentBaseURL?: string;
  attachmentPath?: string;
  hasSharedPath?: boolean;
  csrfToken?: string;
  projectId?: RecordId;
  watchableProjectId?: RecordId;
  users?: UserReferenceObject[];
  watchURL?: string;
  watchCallbackSelector?: string;
  searchURL?: string;
  showAvatar?: boolean;
  publishing?: boolean;
  hasAnnotations?: boolean;
  canFacilitate?: boolean;
  defaultProject?: { id: RecordId; name: string };
  name?: string;
  pageId: RecordId;
  editorMoreApparent?: boolean;
  supportsAssistant?: boolean;
  supportsMentions?: boolean;
  supportsReferences?: boolean;
  placeholderText?: string;
  supportsEmbeddedViews?: boolean;
  supportsEmbeddedViewsViewOnly?: boolean;
  supportsSnippets?: boolean;
  advancedPasteEnabled?: boolean;
  anchorsEnabled?: boolean;
  inline?: boolean;
  linkURL: string;
  linkType: string;
  focusOnMount?: boolean;
  minimumLines?: number;
  setAhaEditor?: (ahaEditor: AhaEditor) => void;
  tableOfContentsEnabled?: boolean;
  disableAttachments?: boolean;
  // This was called disableAttachmentsToolbarItem in v1
  disableAddingAttachments?: boolean;
  usedTemplateIds?: string[];
  documentType?: DocumentTypeEnum_value;
  draftStorageKey?: string;
}

interface AhaEditorFieldV2ControllerState {
  readOnly: boolean;
  editLocked: boolean;
  editingLockedDocument: boolean;
  isAuthenticated: boolean;
  annotateOnly: boolean;
  documentState: DocumentState;
  draft: Draft;
  error: null;
}

class AhaEditorFieldV2Controller extends ApplicationController<
  AhaEditorFieldV2ControllerState,
  AhaEditorFieldV2ControllerProps
> {
  editorController?: AhaEditorV2Controller;
  renderValue: DocumentValue;
  collaborative: boolean;
  clientDocumentId: string;
  collaborationOptions: CollaborationOptions;
  tokenManager: TokenManager;
  fetchUserInfo: FetchUserInfo;
  previousTokenData?: TokenPayload;
  container?: HTMLElement;
  sidePanelController: SidePanelController;
  plugins: EditorPlugin[] = [];
  insertTemplateConfig?: InsertTemplateConfiguration;
  batchedRecordLoader = new BatchedRecordLoader();

  // Document status change related properties
  delayedStateTimers: { [k: string]: ReturnType<typeof setTimeout> | null } =
    {};

  async initialize(props: AhaEditorFieldV2ControllerProps) {
    const { body, value, collaborative, annotateOnly, editLocked, readOnly } =
      props;

    try {
      this.renderValue = AhaEditorUtils.Editor.initializeValue(value, body);
    } catch (error) {
      this.state.error = error;
    }

    this.collaborative = Boolean(collaborative);
    this.state.readOnly = Boolean(readOnly);
    this.state.annotateOnly = Boolean(annotateOnly);
    this.state.editLocked = Boolean(editLocked);
    this.state.editingLockedDocument = false;

    this.initializeCollaboration();

    this.initializePlugins();

    this.initializeInsertTemplates();
  }

  initializeInsertTemplates() {
    if (!this.props.supportsTemplates) return;
    this.insertTemplateConfig = {
      projectId: this.props.projectId,
      showWhiteboardTemplates: this.canAddEmbeddedWhiteboards,
      baseURL: this.props.collaborationBaseURL ?? '',
      getSharingToken: this.props.getSharingToken,
      objectId: this.props.documentId,
      objectType: this.props.objectType,
    };

    const reactivePageId = this.props.hasSharedPath
      ? undefined
      : this.props.pageId;

    this.plugins.push(
      AhaDocumentTemplatePluginFactory({
        controller: this,
        pageId: reactivePageId,
        usedTemplateIds: this.props.usedTemplateIds || [],
      })
    );
  }

  initializeCollaboration() {
    if (!this.collaborative) {
      this.collaborationOptions = { enabled: false };
      return;
    }

    const collabOptions = initializeV2Collaboration({
      documentVersion: this.props.documentVersion,
      documentId: this.props.documentId,
      documentCollection: this.props.documentCollection,
      baseURL: this.props.baseURL!,
      objectType: this.props.objectType,
      tokenDidChange: this.tokenDidChange,
      userId: this.props.userInfo?.userId,
      onError: this.handleCollaborationError,
      collaborationServerURL: this.props.collaborationServerURL,
      collaborationBaseURL: this.props.collaborationBaseURL,
      getSharingToken: this.props.getSharingToken,
      selectionURL: this.props.selectionURL,
      selectionToken: this.props.selectionToken,
    });

    this.tokenManager = collabOptions.tokenManager;
    this.collaborationOptions = collabOptions.collaborationOptions;
    this.fetchUserInfo = collabOptions.fetchUserInfo;
    this.clientDocumentId = this.collaborationOptions.documentId;
  }

  onEditorControllerReady = (editorController: AhaEditorV2Controller) => {
    this.editorController = editorController;

    this.initializeSidePanels();
  };

  initializePlugins() {
    this.plugins = [...RichTextPlugins];

    this.plugins.push(
      // We use 100 on the bottom to handle selection toolbar + tabs in drawer/details
      UiPlugins.SmoothScrollingPlugin({ top: 50, bottom: 100 })
    );

    this.plugins.push(
      SetEditorControllerPlugin({
        setController: this.onEditorControllerReady,
      })
    );

    if (
      this.props.supportsSnippets &&
      this.props.objectType === 'Note' &&
      this.props.parentType !== 'SharedSnippet'
    ) {
      this.plugins.push({
        name: 'Aha Editor Field SharedSnippetProvider',
        provider: new AhaSharedSnippetProvider(this),
      });
    }

    if (this.props.supportsMentions) {
      this.plugins.unshift(
        ...ReferenceUserPlugin({
          projectId: this.props.watchableProjectId,
          watchURL: this.props.watchURL,
          watchCallbackSelector: this.props.watchCallbackSelector,
          searchURL: this.props.searchURL,
          showAvatar: this.props.showAvatar,
        })
      );
    }

    if (this.props.supportsReferences) {
      this.plugins.unshift(
        ...ReferenceAhaPlugin({
          onInsertReference: this.actionCreateRecordLinks.bind(this),
          isModifiable: () => !this.isReadOnly,
          batchedRecordLoader: this.batchedRecordLoader,
        })
      );
    }

    if (window.isKnowledgeBase && window.knowledgeBasePageData) {
      this.plugins.push(KnowledgeBaseLinkRewriterPlugin());
    }

    if (this.canAddEmbeddedWhiteboards) {
      const embeddedWhiteboardPlugin = EmbeddedWhiteboardPlugin({
        containingPageId: this.props.pageId,
        canFacilitate: Boolean(this.props.canFacilitate),
        collaborative: this.isCollaborative,
        commentsEnabled: this.props.hasAnnotations,
        defaultProject: this.props.defaultProject,
        sharingOptions: this.whiteboardSharingOptions,
        getContainer: () => this.container,
      });
      this.plugins.push(...embeddedWhiteboardPlugin);
    }

    if (
      this.props.supportsEmbeddedViews ||
      this.props.supportsEmbeddedViewsViewOnly
    ) {
      const embeddedReportPlugin = EmbeddedReportPlugin({
        allowConfigure: Boolean(this.props.supportsEmbeddedViews),
        advancedPasteEnabled: Boolean(
          this.props.supportsEmbeddedViews && this.props.advancedPasteEnabled
        ),
      });
      // Must be before the link insertData plugin
      this.plugins.unshift(...embeddedReportPlugin);
    } else if (this.props.getSharingToken) {
      const embeddedReportPlugin = BlockedEmbeddedReportPlugin;
      this.plugins.unshift(embeddedReportPlugin);
    }

    if (
      !this.props.disableAddingAttachments &&
      !this.props.disableAttachments
    ) {
      const attachmentPasteTransformerOptions = {
        namePrefix: this.props.name,
        baseURL: this.props.collaborationBaseURL ?? '',
        getSharingToken: this.props.getSharingToken,
        objectType: this.props.objectType,
        objectId: this.props.documentId,
        getEnclosingElement: () => this.enclosingElement,
      };

      const imagePasteTransformerPlugin = attachmentPasteTransformerFactory({
        type: 'image',
        ...attachmentPasteTransformerOptions,
      });
      this.plugins.push(imagePasteTransformerPlugin);

      const videoPasteTransformerPlugin = attachmentPasteTransformerFactory({
        type: 'video',
        ...attachmentPasteTransformerOptions,
      });
      this.plugins.push(videoPasteTransformerPlugin);

      const audioPasteTransformerPlugin = attachmentPasteTransformerFactory({
        type: 'audio',
        ...attachmentPasteTransformerOptions,
      });
      this.plugins.push(audioPasteTransformerPlugin);

      const filePasteTransformerPlugin = attachmentPasteTransformerFactory({
        type: 'attachment',
        ...attachmentPasteTransformerOptions,
      });
      this.plugins.push(filePasteTransformerPlugin);
    }

    this.plugins.push({
      name: 'Aha Editor Field DocumentStateProvider',
      provider: {
        type: 'documentState',
        onDocumentStateChanged: (newState: DocumentState) => {
          unflap(
            'documentState',
            () => {
              this.state.documentState = newState;
            },
            [DocumentState.NOT_CONNECTED, DocumentState.BLOCKED].includes(
              newState
            ),
            15000
          );
        },
      },
    });

    if (!this.props.disableAttachments) {
      this.plugins.push({
        name: 'Aha Editor Field MediaProvider',
        provider: new AhaMediaProvider({
          baseURL: this.props.attachmentBaseURL,
          attachmentPath: this.props.attachmentPath,
          getEnclosingElement: () => this.enclosingElement,
          csrfToken: this.props.csrfToken,
          attachmentBaseURL: this.props.attachmentBaseURL,
          hasSharedPath: this.props.hasSharedPath || false,
          getSharingToken: this.props.getSharingToken,
          canAddAttachments: !this.props.disableAddingAttachments,
        }),
      });

      // Must be before the link insertData plugin
      this.plugins.unshift(...AllPlugins.EmbedPlugin);
    }

    if (this.props.anchorsEnabled) {
      this.plugins.push(
        AnchorLinkPlugin({
          anchorBaseURL: this.props.fullScreenURL
            ? new URL(
                this.props.fullScreenURL,
                window.location.toString()
              ).toString()
            : '',
        })
      );
    }

    if (this.props.hasAnnotations) {
      this.plugins.push(...AllPlugins.AnnotationsPlugin);

      this.plugins.push({
        name: 'Aha Editor Field annotations provider',
        provider: new AhaAnnotationsProvider(
          this,
          this.props.objectType,
          this.props.documentId,
          this.actionCreateRecordLinks.bind(this)
        ),
      });

      this.plugins.push(SelectAnnotationFromUrlPlugin());
    }

    if (this.props.supportsAssistant && this.props.supportsFullscreen) {
      this.plugins.push({
        name: 'Aha! Editor Field WritingAssistantProvider',
        provider: new WritingAssistantProvider({
          onOpenWritingAssistant: this.onOpenWritingAssistant,
        }),
      });
    }

    if (this.props.supportsFullscreen) {
      this.plugins.push(
        FullScreenTopBarPluginFactory({
          getContainer: () => this.container,
          getEnclosingElement: () => this.enclosingElement,
          documentId: this.props.documentId,
          fullScreenURL: this.props.fullScreenURL,
          nonFullScreenURL: this.props.nonFullScreenURL,
        })
      );
    }

    if (this.props.name && !this.isCollaborative && !this.props.readOnly) {
      if (this.props.autosave) {
        this.plugins.push(
          AutosavePlugin({
            autosave: this.props.autosave,
            name: this.props.name,
            format: 'json',
          })
        );
      } else {
        this.plugins.push(
          HiddenFormInputPluginFactory({
            name: this.props.name,
            format: 'json',
          })
        );
      }
    }

    const keyPrefix = this.props.customFieldDefinitionId ?? this.props.name;
    const keyValue = this.props.draftStorageKey;
    if (
      !this.isReadOnly &&
      this.props.saveDrafts &&
      !this.props.autosave &&
      keyPrefix
    ) {
      this.plugins.push(
        DraftManagerPluginFactory({
          keyPrefix,
          keyValue,
        })
      );
    }

    this.plugins.push(
      SidePanelPluginFactory({
        objectType: 'Note',
      })
    );

    if (this.props.tableOfContentsEnabled) {
      this.plugins.push(AllPlugins.TableOfContentsPlugin);

      // In v1, this was also controlled by
      // tableOfContentsEnabled. For consistency, we'll do the same
      // here. Eventually we plan to support these plugins everywhere.
      this.plugins.push(
        ...[AllPlugins.LabelPillPlugin, AllPlugins.InfoBlockPlugin].flat(10)
      );
    }

    if (this.collaborative) {
      this.plugins.push(DocumentSavedStatePlugin);
    }
  }

  changeProps(
    newProps: AhaEditorFieldV2ControllerProps,
    oldProps: AhaEditorFieldV2ControllerProps
  ): void {
    this.state.editLocked = !!newProps.editLocked;
  }

  get isCollaborative(): boolean {
    return Boolean(this.props.collaborative);
  }

  setContainer(container: HTMLElement) {
    this.container = container;
  }

  initializeSidePanels() {
    const { objectType, documentId, collaborationBaseURL, getSharingToken } =
      this.props;

    const sidePanels: SidePanelItem[] = [];

    // We don't display sidepanels when publishing.
    if (this.props.publishing || !this.props.supportsFullscreen) {
      return;
    }

    if (this.props.hasAnnotations) {
      sidePanels.push(
        CommentsSidePanelItem({
          annotatableType: objectType,
          annotatableId: documentId,
          className: 'js-note-annotations-sidebar',
          sidebarId: randomId(),
          baseUrl: collaborationBaseURL,
          isShared: !!getSharingToken,
        })
      );
    }

    if (
      this.props.supportsAssistant &&
      this.props.supportsFullscreen &&
      this.editorController
    ) {
      const assistedEditor = new AssistedEditor(this.editorController);
      assistedEditor.registerSelectionChangePlugin();

      const assistantSidePanel = assistantSidePanelConfiguration({
        agentName: 'WritingAgent',
        agentParams: {
          projectId: this.props.projectId,
        },
        agentInstances: {
          assistedEditor,
        },
      });

      if (assistantSidePanel) sidePanels.push(assistantSidePanel);
    }

    // Don't load best practices templates for shared documents
    if (!this.props.hasSharedPath) {
      const templatePlugin: AhaDocumentTemplatePlugin | undefined =
        this.ahaEditor?.getPlugin(AHA_DOCUMENT_TEMPLATE_PLUGIN_ID);

      const usedTemplateIds = templatePlugin?.api.getUsedTemplateIds() || [];
      if (
        usedTemplateIds.length > 0 &&
        this.props.documentType !== 'MEETING_NOTE'
      ) {
        sidePanels.push(
          BestPracticesSidebarItem(
            usedTemplateIds,
            this.canAddEmbeddedWhiteboards ? 'all' : 'note',
            content => {
              const templatePlugin: AhaDocumentTemplatePlugin | undefined =
                this.ahaEditor?.getPlugin(AHA_DOCUMENT_TEMPLATE_PLUGIN_ID);

              if (templatePlugin && this.ahaEditor) {
                templatePlugin.api.insertExampleContent(
                  this.ahaEditor.editor,
                  content
                );
                this.actionShowSidePanel({
                  open: true,
                  panel: BEST_PRACTICES_PANEL_KEY,
                });
              }
            }
          )
        );
      }
    }

    const sidePanelProvider = this.ahaEditor?.getProvider('sidePanel');
    sidePanelProvider?.setSidePanels(sidePanels);
  }

  actionSetSidePanelController(sidePanelController: SidePanelController): void {
    this.sidePanelController = sidePanelController;
  }

  onOpenWritingAssistant = function onOpenWritingAssistant(): void {
    this.actionShowSidePanel({ panel: 'assistant', open: true });
  }.bind(this);

  get $annotationsSidebar(): JQuery | undefined {
    if (!this.enclosingElement) return;

    return $(this.enclosingElement).find('.js-note-annotations-sidebar:first');
  }

  get $annotatableElement(): JQuery | undefined {
    if (!this.enclosingElement) return;

    return $(this.enclosingElement).find('.js-annotatable:first');
  }

  get enclosingElement() {
    if (!this.container || !this.props.enclosingSelector) return;

    const el = this.container.closest<HTMLElement>(
      this.props.enclosingSelector
    );

    if (el) {
      return el;
    }
  }

  get editorState() {
    if (!this.editorController) {
      return;
    }

    return this.editorController.state;
  }

  get pluginState() {
    return this.editorState?.pluginState;
  }

  get ahaEditor() {
    if (!this.editorController) {
      return;
    }

    return this.editorController.ahaEditor;
  }

  get isReadOnly(): boolean {
    return this.state.readOnly || !this.canMakeChanges;
  }

  get isLocked(): boolean {
    return this.state.editLocked && !this.state.editingLockedDocument;
  }

  get isDocumentConnected(): boolean {
    const { NOT_CONNECTED, BLOCKED } = DocumentState;
    return ![NOT_CONNECTED, BLOCKED].includes(this.state.documentState);
  }

  get canMakeChanges(): boolean {
    if (!this.editorController?.isCollaborative) {
      return true;
    }

    return this.state.isAuthenticated && this.isDocumentConnected;
  }

  get showBorder(): Behaviors['showBorder'] {
    if (/\baha-editor-field-form\b/.test(this.props.className ?? '')) {
      return true;
    }

    if (this.isReadOnly) {
      return false;
    }

    return 'focused';
  }

  tokenDidChange = (newTokenData: TokenPayload) => {
    if (newTokenData) {
      this.state.isAuthenticated = true;

      if (!newTokenData.allow_update) {
        if (newTokenData.allow_annotate && !this.state.annotateOnly) {
          this.state.annotateOnly = true;
          if (this.previousTokenData) {
            console.warn('Switching to annotate-only mode, rolling back');
            // @ts-expect-error collaborativeDocument exists on the controller
            this.editorController.collaborativeDocument.rollback();
          }
        } else if (!newTokenData.allow_annotate && !this.state.readOnly) {
          this.state.readOnly = true;
          if (this.previousTokenData) {
            console.warn('Switching to read-only mode, rolling back');
            // @ts-expect-error collaborativeDocument exists on the controller
            this.editorController.collaborativeDocument.rollback();
          }
        }
      }

      this.previousTokenData = newTokenData;
    } else {
      this.state.isAuthenticated = false;
    }
  };

  handleCollaborationError = (
    error: { code: number },
    onRecovered: () => void
  ): boolean => {
    // Token was invalid / not found (4040) or expired (4035). Try for a new one.
    if ([4035, 4040].includes(error.code)) {
      if (this.tokenManager) {
        this.tokenManager.token = null;
        this.tokenManager.throttledRenewToken(onRecovered);
      } else {
        console.error(
          'Had a null token manager during recovery, this should never happen'
        );
      }
      return true;
    }

    // We're missing ops we need to catch up to the latest
    // version. The only thing we can do is force a document reset to
    // the newest version.
    if ([5101].includes(error.code)) {
      if (this.editorController) {
        console.warn('Missing ops needed to catch up, rolling back');
        // @ts-expect-error collaborativeDocument exists on the controller
        this.editorController.collaborativeDocument.rollback();
      } else {
        console.warn(
          "Missing ops but editor isn't currently mounted, ignoring"
        );
      }
      // eslint-disable-next-line no-unsafe-finally
      return true;
    }

    // We have a generic database error and its unknown why
    if ([5103].includes(error.code)) {
      this.state.readOnly = true;
      showAlertMessage(
        'We were not able to load the editor. Please refresh the page and try again. If this problem continues, please contact support@aha.io.'
      );
      // eslint-disable-next-line no-unsafe-finally
      return true;
    }

    return false;
  };

  get whiteboardSharingOptions() {
    return {
      connectedAccountId: this.props.connectedAccountId,
      collaborationBaseURL: this.props.collaborationBaseURL,
      collaborationServerURL: this.props.collaborationServerURL,
      csrfToken: this.props.csrfToken,
      baseURL: this.props.baseURL,
      getSharingToken: this.props.getSharingToken,
      selectionURL: this.props.selectionURL,
    };
  }

  destroy() {
    if (this.tokenManager) this.tokenManager.disconnect();
    clearUnflap('documentState');
  }

  actionShowSidePanel(
    options: { open: false } | { open: true; panel: string } = { open: false }
  ) {
    const sidePanelProvider = this.ahaEditor?.getProvider('sidePanel');

    if (options.open) {
      sidePanelProvider?.onOpenSidePanel(options.panel);
    } else {
      sidePanelProvider?.onCloseSidePanel();
    }
  }

  private triggerResizeEvent() {
    window.dispatchEvent(new Event('resize'));
  }

  actionUnlockDocument() {
    this.state.editingLockedDocument = true;
  }

  get allowedMediaRegex() {
    return new RegExp(
      `^${window.location.protocol}//${window.location.host}/(develop/|empathy/)?attachments/\\d+/token/[0-9a-f]+\\.download`
    );
  }

  get canAddEmbeddedWhiteboards() {
    return !!this.props.supportsEmbeddedWhiteboards && !!this.props.documentId;
  }

  actionHandleError(error: Error) {
    if (error instanceof MigrationError) {
      showAlertMessage(
        'An unhandled error occurred. Please contact support@aha.io for help.'
      );
      console.error(error.message);
    } else {
      throw error;
    }
  }

  actionCreateRecordLinks(
    reference: ReferenceAhaObject,
    annotationId?: RecordId
  ) {
    if (!this.props.linkURL) return;

    if (['Competitor', 'Persona'].includes(reference.class)) {
      return;
    }

    $.ajax({
      url: this.props.linkURL,
      type: 'POST',
      data: {
        record_link: {
          record_ids: reference.id,
          record_reference_nums: reference.reference_num, // NOTE: This is added for convert-on-paste reference links where we don't have the record id
          record_type: reference.class,
          link_type: this.props.linkType,
        },
        annotation_id: annotationId,
      },
    });
  }

  get documentId(): RecordId {
    return this.props.documentId;
  }
}

export default AhaEditorFieldV2Controller;
